import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import InstitutionalBlurbSchema from './schema';
import defaultBlurbItems from './defaultItems';
import InstitutionalBlurbBody from './InstitutionalBlurbBody';
import type { InstitutionalBlurbData } from './InstitutionalBlurbBody';

type InstitutionalBlurbEditProps = {
  data: InstitutionalBlurbData;
  block: string;
  selected: boolean;
  onChangeBlock: (block: string, data: InstitutionalBlurbData) => void;
};

const InstitutionalBlurbEdit = (props: InstitutionalBlurbEditProps) => {
  const { data, block, onChangeBlock, selected } = props;
  const intl = useIntl();
  const schema = InstitutionalBlurbSchema({ intl });

  // Preenche a faixa no idioma do editor quando o bloco chega SEM `items` —
  // o que só acontece pelo template da notícia (config/newsTemplate.ts), que é
  // config estática e não tem acesso ao `intl`. Sem isto, uma notícia criada em
  // `/en` nascia com a faixa em português.
  //
  // ⚠️ O core desaconselha chamar `onChangeBlock` "at creation time", por risco
  // de corrida na gravação dos blocos (comentário do `applyBlockInitialValue`
  // em helpers/Blocks/Blocks.js). Aqui não é na criação: é um efeito DEPOIS da
  // montagem, quando o formulário já montou todos os blocos, e roda uma única
  // vez por bloco graças ao ref.
  //
  // A distinção entre `undefined` e `[]` é o que faz a remoção grudar: quem
  // apagou todos os parceiros fica com `[]` e não vê o texto voltar.
  const jaPreencheu = useRef(false);
  useEffect(() => {
    if (jaPreencheu.current || data.items !== undefined) return;
    jaPreencheu.current = true;
    onChangeBlock(block, { ...data, items: defaultBlurbItems({ intl }) });
  }, [block, data, intl, onChangeBlock]);

  return (
    <>
      <InstitutionalBlurbBody data={data} isEditMode />
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id: string, value: unknown) => {
            onChangeBlock(block, { ...data, [id]: value });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

export default InstitutionalBlurbEdit;
